import { useState, useRef, useCallback, useEffect } from 'react';
import { executeCode } from '../../services/codeRunner';

/**
 * useRunner — custom hook for executing code via the backend code runner API.
 */
export default function useRunner() {
    const [output, setOutput] = useState([]);
    const [running, setRunning] = useState(false);
    const abortControllerRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // ── Run code via backend ──
    const runCode = useCallback(
        async (language, code, stdin) => {
            if (!code || !code.trim()) {
                setOutput([{ type: 'warn', text: '⚠ No code to run.' }]);
                return;
            }

            // Cancel any ongoing run
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            setRunning(true);
            setOutput([{ type: 'info', text: '▶ Running...' }]);

            try {
                const response = await executeCode({ language, code, stdin });

                // Check if this request was aborted
                if (controller.signal.aborted) return;

                if (!response.success) {
                    setOutput([
                        { type: 'error', text: `❌ ${response.error || 'Code execution failed.'}` }
                    ]);
                    setRunning(false);
                    return;
                }

                const newOutput = [];

                // 1. If compiler output (build output) exists, show it
                if (response.compile_output && response.compile_output.trim()) {
                    newOutput.push({
                        type: response.exit_code === 0 ? 'warn' : 'error',
                        label: 'Compiler Output',
                        text: response.compile_output
                    });
                }

                // 2. If standard output exists, show it
                if (response.stdout && response.stdout.trim()) {
                    newOutput.push({
                        type: 'default',
                        text: response.stdout
                    });
                }

                // 3. If standard error exists, show it
                if (response.stderr && response.stderr.trim()) {
                    newOutput.push({
                        type: 'error',
                        label: 'Runtime Error',
                        text: response.stderr
                    });
                }

                // 4. Show execution summary badge / status line
                const timeStr = response.execution_time ? response.execution_time : '0.00s';
                const memStr = response.memory ? response.memory : '0KB';
                const exitCodeVal = response.exit_code !== null ? response.exit_code : 'N/A';
                
                newOutput.push({
                    type: (response.exit_code !== null && Number(response.exit_code) === 0) ? 'success' : 'error',
                    text: `Time: ${timeStr}  •  Memory: ${memStr}  •  Exit: ${exitCodeVal}`
                });

                setOutput(newOutput);
                setRunning(false);
            } catch (err) {
                if (controller.signal.aborted) return;
                setOutput([
                    { type: 'error', text: `❌ Unexpected error: ${err.message || err}` }
                ]);
                setRunning(false);
            } finally {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                }
            }
        },
        []
    );

    // ── Stop execution (abort fetch) ──
    const stopExecution = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            setOutput((prev) => [...prev, { type: 'warn', text: '⛔ Execution stopped.' }]);
        }
        setRunning(false);
    }, []);

    // ── Clear output ──
    const clearOutput = useCallback(() => {
        setOutput([]);
    }, []);

    return { output, running, runCode, stopExecution, clearOutput };
}
